import { useState } from "react";
import FadeInSection from "../../../components/feature/FadeInSection";

export default function ContactSection() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setStatus("sending");
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      if (typeof value === "string") data.append(key, value);
    });

    try {
      const res = await fetch("https://readdy.ai/api/form/d729os4mc4tbhfnc6b50", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="bg-white py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left */}
          <FadeInSection direction="left">
            <div>
              <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#3b6fd4] mb-3">
                Contact
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0f2559] mb-4">
                Let&apos;s talk about{" "}
                <span className="text-slate-400">what Ben can do for you.</span>
              </h2>
              <p className="text-slate-500 text-base leading-relaxed mb-8">
                Have a question? Want to learn more about Agent Ben or get a personalized demo? Drop us a message and we&apos;ll get back to you quickly.
              </p>

              <div className="flex flex-col gap-4">
                {[
                  { icon: "ri-mail-line", label: "Email", value: "support@cloudsdevs.com" },
                  { icon: "ri-customer-service-2-line", label: "Support", value: "Available 24/7 via Agent Ben" },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#0f2559]/8 rounded-lg">
                      <i className={`${item.icon} text-[#0f2559] text-base`} />
                    </div>
                    <div>
                      <p className="text-slate-400 text-xs">{item.label}</p>
                      <p className="text-[#0f2559] text-sm font-semibold">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 bg-gradient-to-br from-[#0f2559] to-[#1a3a6e] rounded-2xl p-6 text-white">
                <div className="w-10 h-10 flex items-center justify-center mb-4">
                  <img
                    src="https://static.readdy.ai/image/b0dfad47411a5cab4bb37e29cd654d91/ad7ef5d5c8ae0c30ee62e19ca8db67c2.png"
                    alt="Agent Ben"
                    className="w-10 h-10 object-contain rounded-full"
                  />
                </div>
                <p className="text-white/90 text-sm font-semibold mb-1">Ready to meet Agent Ben?</p>
                <p className="text-white/50 text-xs leading-relaxed">
                  Send us a message and we&apos;ll show you exactly how Agent Ben can transform your workflow.
                </p>
              </div>
            </div>
          </FadeInSection>

          {/* Right - Form */}
          <FadeInSection direction="right" delay={100}>
            <div className="bg-[#f5f8ff] border border-[#dce6f7] rounded-2xl p-8">
              {status === "success" ? (
                <div className="flex flex-col items-center justify-center py-12 gap-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#0f2559]/10 rounded-full">
                    <i className="ri-check-line text-[#0f2559] text-2xl" />
                  </div>
                  <h3 className="text-[#0f2559] font-semibold text-lg">Message Sent!</h3>
                  <p className="text-slate-500 text-sm text-center">
                    Thanks for reaching out. We&apos;ll get back to you soon.
                  </p>
                  <button
                    onClick={() => setStatus("idle")}
                    className="mt-2 text-[#3b6fd4] text-sm hover:text-[#0f2559] cursor-pointer whitespace-nowrap font-medium"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="agent-ben-contact"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-slate-500 text-xs font-semibold">Name</label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="Your name"
                        className="bg-white border border-[#dce6f7] rounded-lg px-4 py-3 text-sm text-[#0f2559] placeholder-slate-300 outline-none focus:border-[#3b6fd4] transition-colors duration-200"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-slate-500 text-xs font-semibold">Email</label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="you@example.com"
                        className="bg-white border border-[#dce6f7] rounded-lg px-4 py-3 text-sm text-[#0f2559] placeholder-slate-300 outline-none focus:border-[#3b6fd4] transition-colors duration-200"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-slate-500 text-xs font-semibold">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      required
                      placeholder="What is this about?"
                      className="bg-white border border-[#dce6f7] rounded-lg px-4 py-3 text-sm text-[#0f2559] placeholder-slate-300 outline-none focus:border-[#3b6fd4] transition-colors duration-200"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between">
                      <label className="text-slate-500 text-xs font-semibold">Message</label>
                      <span className={`text-xs ${charCount > 500 ? "text-red-500" : "text-slate-400"}`}>
                        {charCount}/500
                      </span>
                    </div>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="Tell us how Agent Ben can help you..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="bg-white border border-[#dce6f7] rounded-lg px-4 py-3 text-sm text-[#0f2559] placeholder-slate-300 outline-none focus:border-[#3b6fd4] transition-colors duration-200 resize-none"
                    />
                  </div>

                  {status === "error" && (
                    <p className="text-red-500 text-xs">Something went wrong. Please try again.</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "sending" || charCount > 500}
                    className="bg-[#0f2559] hover:bg-[#1a3a6e] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
                  >
                    {status === "sending" ? "Sending..." : "Send Message"}
                  </button>
                </form>
              )}
            </div>
          </FadeInSection>
        </div>
      </div>
    </section>
  );
}
